import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const NotificationContainer = styled(motion.div)`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  max-width: 400px;
  min-width: 300px;
`;

const NotificationCard = styled(motion.div)`
  background: ${props => {
    switch (props.type) {
      case 'success':
        return 'linear-gradient(135deg, #4CAF50, #45a049)';
      case 'error':
        return 'linear-gradient(135deg, #f44336, #da190b)';
      case 'warning':
        return 'linear-gradient(135deg, #ff9800, #f57c00)';
      case 'info':
      default:
        return 'linear-gradient(135deg, #2196F3, #1976D2)';
    }
  }};
  color: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  animation: ${slideIn} 0.3s ease-out;
`;

const NotificationContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const NotificationIcon = styled.div`
  font-size: 20px;
  margin-right: 12px;
`;

const NotificationText = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
`;

const NotificationTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  margin-left: 12px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-left: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
`;

const ActionContainer = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
`;

const getIcon = (type) => {
  switch (type) {
    case 'success':
      return '✅';
    case 'error':
      return '❌';
    case 'warning':
      return '⚠️';
    case 'info':
    default:
      return 'ℹ️';
  }
};

let notificationId = 0;

class NotificationManager {
  constructor() {
    this.notifications = [];
    this.listeners = [];
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  emit() {
    this.listeners.forEach(listener => listener(this.notifications));
  }

  show({ type = 'info', title, message, duration = 5000, actions = [] }) {
    const id = ++notificationId;
    const notification = { id, type, title, message, duration, actions };
    
    this.notifications.push(notification);
    this.emit();

    if (duration > 0) {
      setTimeout(() => {
        this.remove(id);
      }, duration);
    }

    return id;
  }

  remove(id) {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.emit();
  }

  clear() {
    this.notifications = [];
    this.emit();
  }

  success(message, options = {}) {
    return this.show({ type: 'success', message, ...options });
  }

  error(message, options = {}) {
    return this.show({ type: 'error', message, ...options });
  }

  warning(message, options = {}) {
    return this.show({ type: 'warning', message, ...options });
  }

  info(message, options = {}) {
    return this.show({ type: 'info', message, ...options });
  }
}

export const notificationManager = new NotificationManager();

const NotificationItem = ({ notification, onClose }) => {
  const { id, type, title, message, actions } = notification;

  return (
    <NotificationCard
      type={type}
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <NotificationContent>
        <NotificationIcon>{getIcon(type)}</NotificationIcon>
        <div style={{ flex: 1 }}>
          {title && <NotificationTitle>{title}</NotificationTitle>}
          <NotificationText>{message}</NotificationText>
          {actions.length > 0 && (
            <ActionContainer>
              {actions.map((action, index) => (
                <ActionButton
                  key={index}
                  onClick={() => {
                    action.onClick();
                    onClose(id);
                  }}
                >
                  {action.label}
                </ActionButton>
              ))}
            </ActionContainer>
          )}
        </div>
      </NotificationContent>
      <CloseButton onClick={() => onClose(id)}>
        ×
      </CloseButton>
    </NotificationCard>
  );
};

const NotificationProvider = () => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const unsubscribe = notificationManager.subscribe(setNotifications);
    return unsubscribe;
  }, []);

  const handleClose = (id) => {
    notificationManager.remove(id);
  };

  return (
    <NotificationContainer>
      <AnimatePresence>
        {notifications.map(notification => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClose={handleClose}
          />
        ))}
      </AnimatePresence>
    </NotificationContainer>
  );
};

export default NotificationProvider; 